/**
 * useConflictGate — перехват 409 с conflicts[] от гейта бронирований
 * (POST /api/bookings, PUT /api/bookings/move|restore) и staff-повтор
 * ТОГО ЖЕ запроса с force:true (бэк пишет audit forced_* с attention).
 *
 * run(submit, label): вызывает submit(); ApiException 409 с непустым
 * body.conflicts перехватывается — конфликты и замыкание сохраняются,
 * возврат true («показать ConflictOverride»). Всё остальное пробрасывается
 * наверх, в существующий catch вызывающего — включая 409 БЕЗ conflicts
 * (точный дубль слота: его force НЕ продавливает, кнопки быть не должно).
 *
 * retry(): тот же submit с force=true; ошибки повтора уходят в onError.
 */
import { useCallback, useMemo, useRef, useState } from "react";
import { ApiException, type ApiConflict } from "../api/types";

interface GateState {
  conflicts: ApiConflict[];
  label: string;
}

export interface ConflictGate {
  /** null — гейт закрыт (саб-модал не показывается). */
  conflicts: ApiConflict[] | null;
  /** Подпись danger-кнопки, переданная в run (напр. «ВСЁ РАВНО ПЕРЕНЕСТИ»). */
  actionLabel: string;
  /** true — пойман 409 с conflicts (открыт override), false — успех. */
  run: (submit: (force?: boolean) => Promise<void>, label: string) => Promise<boolean>;
  /** Force-повтор сохранённого submit; после успеха ведёт себя как обычный успех. */
  retry: () => Promise<void>;
  clear: () => void;
}

export function useConflictGate(onError: (err: unknown) => void): ConflictGate {
  const [state, setState] = useState<GateState | null>(null);
  const submitRef = useRef<((force?: boolean) => Promise<void>) | null>(null);
  /* ref — чтобы retry был стабилен даже при inline-колбэке onError. */
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const run = useCallback(
    async (submit: (force?: boolean) => Promise<void>, label: string): Promise<boolean> => {
      try {
        await submit();
        return false;
      } catch (err) {
        const conflicts =
          err instanceof ApiException && err.status === 409 ? err.body?.conflicts : undefined;
        if (!conflicts?.length) throw err;
        submitRef.current = submit;
        setState({ conflicts, label });
        return true;
      }
    },
    [],
  );

  const retry = useCallback(async () => {
    const submit = submitRef.current;
    submitRef.current = null;
    setState(null);
    if (!submit) return;
    try {
      await submit(true);
    } catch (err) {
      onErrorRef.current(err);
    }
  }, []);

  const clear = useCallback(() => {
    submitRef.current = null;
    setState(null);
  }, []);

  return useMemo(
    () => ({
      conflicts: state?.conflicts ?? null,
      actionLabel: state?.label ?? "",
      run,
      retry,
      clear,
    }),
    [state, run, retry, clear],
  );
}
